import { useContext, useEffect, useState, useMemo } from "react"
import { Stack } from "@mui/material"
import moment from "moment"
import {
    Button,
    DataTable,
    DialogHandler,
    EmptyContent,
    fetchRoute,
    GenericWrapperContext,
    LoadingContainer,
    Search,
    TDataTableColumnsData,
    TGenericListReturn,
    FullBar,
    Header,
    Icon,
    UsersFactory,
    UserModel,
    TOrder,
    TDataTableRowData,
    amIImpersonating
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import { TUsersListing, TUserExpirationInfo } from "../types"
import { ERoutes } from "../../../utils/url"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { UserRow } from "../components/userRow"
import { hasPageElementAccess, EPageCustomization } from "../../../utils/limits_features"

export function UsersListing({ setAccount }: TUsersListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()

    // Brand customization flags
    const userDeleteDisabled = hasPageElementAccess({ pageElement: EPageCustomization.userDeleteDisabled, partnerBrand: genericWrapper.partnerBrand })
    const userCreateDisabled = hasPageElementAccess({ pageElement: EPageCustomization.userCreateDisabled, partnerBrand: genericWrapper.partnerBrand })

    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState<string>("")
    const [openedSearch, setOpenedSearch] = useState(false)
    const [order, setOrder] = useState<TOrder>("asc")
    const [orderBy, setOrderBy] = useState<string>("created_on")
    const [users, setUsers] = useState<TGenericListReturn<UserModel>>()

    useEffect(() => {
        fetchUsers()
    }, [])

    useEffect(handleSearchFocus, [openedSearch])

    function fetchUsers() {
        setLoading(true)

        // Get active users first
        const options: any = {
            withCount: true
        }

        UsersFactory.list(options).then((activeUsers) => {
            // Get pending users (no pagination)
            const pendingOptions: any = {
                withCount: true,
                filter: "status==pending"
            }

            UsersFactory.list(pendingOptions).then((pendingUsers) => {
                const combinedUsers = {
                    ...activeUsers,
                    data: activeUsers.data.concat(pendingUsers.data),
                    pagination: {
                        ...activeUsers.pagination,
                        count: (activeUsers.pagination?.count || 0) + (pendingUsers.pagination?.count || 0)
                    }
                }

                setUsers(combinedUsers)
                setLoading(false)
            }).catch(() => {
                // If pending call fails, just set active users
                setUsers(activeUsers)
                setLoading(false)
            })
        }).catch(() => {
            setLoading(false)
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
        })
    }

    function getSortedUsers() {
        if (!users?.data) return []

        let userList = [...users.data]

        // All results are already fetched above (active + pending users, not paginated)
        // This avoids making additional API calls with filters like:
        // "email==email;status==pending" (pending users matching email)
        // "email==email" (active users matching email - default when status not specified)
        if (search) {
            const searchLower = search.toLowerCase()
            userList = userList.filter((user) =>
                user.email?.toLowerCase().includes(searchLower)
            )
        }

        const pendings = userList.filter((user) => user.status === "pending")
        const active = userList.filter((user) => user.status === "active")

        const sortUsers = (users: UserModel[]) => {
            return users.sort((a, b) => {
                if (orderBy === 'created_on') {
                    const aValue = a.created_on || 0
                    const bValue = b.created_on || 0
                    return order === 'asc' ? aValue - bValue : bValue - aValue
                } else if (orderBy === 'name') {
                    const nameA = `${a.first_name || ""} ${a.last_name || ""}`.trim().toLowerCase()
                    const nameB = `${b.first_name || ""} ${b.last_name || ""}`.trim().toLowerCase()
                    return order === 'asc'
                        ? nameA.localeCompare(nameB)
                        : nameB.localeCompare(nameA)
                } else if (orderBy === 'email') {
                    const emailA = (a.email || "").toLowerCase()
                    const emailB = (b.email || "").toLowerCase()
                    return order === 'asc'
                        ? emailA.localeCompare(emailB)
                        : emailB.localeCompare(emailA)
                } else if (orderBy === 'last_activity_on') {
                    const aValue = a.last_activity_on || 0
                    const bValue = b.last_activity_on || 0
                    return order === 'asc' ? aValue - bValue : bValue - aValue
                }
                return 0
            })
        }

        sortUsers(pendings)
        sortUsers(active)

        // Always return pending users first, then active users
        return pendings.concat(active)
    }


    function handleAddUser() {
        if (userCreateDisabled) {
            DialogHandler.show({
                isExpress: true,
                type: "warning",
                title: t("users-user-create-disabled")
            })
            return
        }
        window.location.href = "#" + fetchRoute({ route: ERoutes.createUser })
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handleViewUser(user: UserModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.userProfile, params: { userId: user.id } })
    }

    function handleGiveOwnership(user: UserModel) {
        if (!genericWrapper.account) return

        setLoading(true)
        genericWrapper.account.update({
            id: genericWrapper.account.id,
            account_owner: {
                user_id: user.id
            }
        }).then((updatedAccount) => {
            setAccount(updatedAccount.data)
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("users-user-updated-success")
            })
            setLoading(false)
        }).catch((error) => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
            setLoading(false)
        })
    }

    function handleDeleteUser(user: UserModel) {
        // Check if delete is disabled by brand customization
        if (userDeleteDisabled) {
            DialogHandler.show({
                isExpress: true,
                type: "warning",
                title: t("users-user-delete-disabled")
            })
            return
        }

        // Check if user is trying to delete themselves or the account owner
        if (user.email === genericWrapper.user.email) {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
            return
        }

        if (user.id === genericWrapper.account?.account_owner?.user_id) {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("general-error-action")
            })
            return
        }

        showDeleteDialog({
            name: user.email || "",
            resourceKey: "general-user_one"
        }).then(({ btnRef }) => {
            btnRef.animate()
            return user.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("users-user-delete-success")
            })
            fetchUsers()
        }).catch((e) => {
            if (e)
                DialogHandler.show({
                    isExpress: true,
                    type: "warning",
                    title: t("general-error-action")
                })
        })
    }

    function handleResendVerificationEmail(user: UserModel) {
        setLoading(true)
        user.resendVerificationEmail().then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("users-user-resend-verification-done")
            })
        }).catch(() => {
            DialogHandler.show({
                isExpress: true,
                type: "error",
                title: t("users-user-resend-verification-error")
            })
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)

        // mimic search delay
        if (cSearch) {
            setLoading(true)
            setTimeout(() => {
                setLoading(false)
            }, 300)
        } else {
            setLoading(false)
        }
    }


    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".clientSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchOpen() {
        setOpenedSearch(!openedSearch)
    }

    function getUserExpirationInfo(user: UserModel): TUserExpirationInfo {
        let isExpired = false
        let daysLeft = 0
        let expirationMessage = ""

        if (user.status === "pending" && user.expires_on && typeof user.expires_on === 'number') {
            const now = moment()
            const expiresOn = moment.unix(user.expires_on)
            daysLeft = expiresOn.diff(now, "days")

            if (daysLeft < 0) {
                isExpired = true
                expirationMessage = t("users-listing-user-invites-expired")
            } else if (daysLeft === 0) {
                expirationMessage = t("users-listing-user-invites-expires-today")
            } else {
                expirationMessage = t("users-listing-user-invites-expires-in", { days: daysLeft })
            }
        }

        return { isExpired, daysLeft, expirationMessage }
    }

    const rowDropdownOptions: any[] = useMemo(() => {
        const options: any[] = []
        const accountOwnerId = genericWrapper.account?.account_owner?.user_id

        // add delete option if not disabled
        if (!userDeleteDisabled) {
            options.push({
                isAction: true,
                value: "delete-user",
                text: t("general-delete"),
                onClick: handleDeleteUser
            })
        }

        // add give ownership option if no owner exists or user is impersonating
        if (!accountOwnerId || amIImpersonating()) {
            options.push({
                isAction: true,
                value: "give-ownership",
                text: t("users-user-become-owner"),
                onClick: handleGiveOwnership
            })
        }

        return options
    }, [genericWrapper.account?.account_owner?.user_id])

    const colsData: TDataTableColumnsData[] = useMemo(() => [
        {
            id: "name",
            label: t("general-user_one"),
            sortable: true
        },
        {
            id: "email",
            label: t("general-email-address"),
            sortable: true
        },
        {
            id: "last_activity_on",
            label: t("general-last_seen"),
            sortable: true
        },
        {
            id: "created_on",
            label: t("general-created_on"),
            sortable: true
        },
        {
            id: "owner",
            label: t("users-listing-column-heading-owner")
        },
        {
            id: "status",
            label: t("users-listing-column-heading-status")
        },
        {
            id: "actions",
            label: ""
        }
    ], [])

    const rows: any[] = getSortedUsers().filter(user => user).map((user: UserModel, index) => {
        const isOwner = user.id === genericWrapper.account?.account_owner?.user_id
        const isCurrentUser = user.email === genericWrapper.user?.email
        const { isExpired, daysLeft, expirationMessage } = getUserExpirationInfo(user)

        return UserRow({
            user,
            index,
            loading,
            isOwner,
            isCurrentUser,
            isExpired,
            daysLeft,
            expirationMessage,
            rowDropdownOptions,
            handleViewUser,
            handleResendVerificationEmail,
            t // have to pass t from parent
        })
    }) || []

    return (
        <Stack gap={8}>
            <FullBar
                className={loading ? "loading" : ""}
                left={<Header
                    heading={t("general-user_other")}
                    headingVariant="titleL"
                    support={genericWrapper.account?.name}
                    headingTooltip={t("users-listing-heading-tooltip")}
                />}
                right={[
                    <Button
                        key="add-user"
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleAddUser}
                        iconOnly
                        disabled={loading || userCreateDisabled}
                    >
                        <Icon name="Add18" />
                    </Button>,
                    <Button
                        key="search-users"
                        variant="outlined"
                        size="medium"
                        color="body1"
                        onClick={handleSearchOpen}
                        iconOnly
                        disabled={loading}
                    >
                        <Icon name="Search18" />
                    </Button>
                ]}
            />

            {openedSearch &&
                <Search
                    placeholder={t("users-listing-search-placeholder")}
                    initValue={search}
                    className={`suppressionListSearch ${loading ? "loading" : ""}`}
                    onChange={handleSearchChange}
                    onClose={handleSearchOpen}
                    useClickAway
                />
            }

            <LoadingContainer loading={!users}>

                {users && users?.data?.length === 0 && !search &&
                    <EmptyContent
                        title={t("users-listing-empty-title")}
                        desc={t("users-listing-empty-desc")}
                        iconName="CreateForm44"
                        button={!userCreateDisabled ? <Button
                            variant="contained"
                            onClick={handleAddUser}
                            size="medium"
                            color="secondary"
                            iconOnly>
                            {`+ ${t('general-add-user')}`}
                        </Button> : undefined}
                    />
                }

                {users && users.data.length === 0 && search &&
                    <EmptyContent
                        title={t("users-listing-empty-search-title")}
                        desc={t("users-listing-empty-search-desc")}
                    />
                }

                {users && users.data.length > 0 &&
                    <DataTable
                        initState={{
                            order,
                            orderBy
                        }}
                        totalCount={users.pagination.count}
                        className={`suppressionListDataTable ${loading ? "loading" : ""}`}
                        onSortChange={handleSortChange}
                        rows={rows}
                        columnsData={colsData}
                        stickyLastCol
                        showPagination={false}
                    />
                }
            </LoadingContainer>
        </Stack>
    )
}